// MentionBell — navbar bell with unread mention count + dropdown of recent
// mentions. Clicking a mention navigates to its source and marks it read.
import { useMemo, useState } from "react";
import { Bell, MessageSquare, ListChecks } from "lucide-react";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMentions } from "@/hooks/useMentions";
import { useProfilesByIds } from "@/hooks/useProfilesByIds";
import { useMySquads } from "@/hooks/useSquads";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState as useReactState } from "react";
import { cn } from "@/lib/utils";

const MentionBell = () => {
  const { user } = useAuth();
  const { mentions, counts, markRead } = useMentions();
  const { data: squads = [] } = useMySquads();
  const [open, setOpen] = useState(false);

  const authorIds = useMemo(
    () => Array.from(new Set(mentions.slice(0, 10).map((m) => m.mentioned_by))),
    [mentions],
  );
  const profiles = useProfilesByIds(authorIds);

  // Map squad_id → slug so we can build deep links.
  const squadSlugById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of squads) map.set(s.id, s.slug);
    return map;
  }, [squads]);

  // We also need jam_id → slug for jam mentions. Cache lazily via a single
  // query that includes only the jams referenced by the recent mentions.
  const [jamSlugById, setJamSlugById] = useReactState<Map<string, string>>(new Map());
  useEffect(() => {
    const jamIds = Array.from(
      new Set(mentions.map((m) => m.jam_id).filter((x): x is string => !!x)),
    );
    if (jamIds.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("jams")
        .select("id, slug")
        .in("id", jamIds);
      if (cancelled || !data) return;
      const map = new Map<string, string>();
      for (const j of data) map.set(j.id, j.slug);
      setJamSlugById(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [mentions]);

  if (!user) return null;

  const recent = mentions.slice(0, 8);
  const unread = counts.total;

  const linkFor = (m: (typeof mentions)[number]): string | null => {
    if (m.source_kind === "squad_message" && m.squad_id) {
      const slug = squadSlugById.get(m.squad_id);
      return slug ? `/squads/${slug}` : null;
    }
    if (m.source_kind === "jam_task" && m.jam_id) {
      const slug = jamSlugById.get(m.jam_id);
      return slug ? `/jams/${slug}` : null;
    }
    return null;
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        className="relative flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
        aria-label={
          unread > 0 ? `${unread} unread mention${unread === 1 ? "" : "s"}` : "Mentions"
        }
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-mono font-bold flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 font-mono p-0">
        <div className="flex items-center justify-between px-3 py-2">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono p-0">
            Mentions {unread > 0 && `· ${unread} new`}
          </DropdownMenuLabel>
          {unread > 0 && (
            <button
              onClick={() => markRead("all")}
              className="text-[10px] text-primary hover:text-primary/80"
            >
              Mark all read
            </button>
          )}
        </div>
        <DropdownMenuSeparator className="m-0" />
        {recent.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">
            You're all caught up.
          </div>
        ) : (
          <div className="max-h-80 overflow-auto">
            {recent.map((m) => {
              const author = profiles[m.mentioned_by];
              const authorName = author?.display_name ?? author?.username ?? "someone";
              const href = linkFor(m);
              const Icon = m.source_kind === "squad_message" ? MessageSquare : ListChecks;
              const inner = (
                <div
                  className={cn(
                    "flex gap-2 px-3 py-2 transition-colors hover:bg-accent/40 cursor-pointer",
                    !m.read_at && "bg-primary/5",
                  )}
                  onClick={() => {
                    void markRead("mention", m.id);
                    setOpen(false);
                  }}
                >
                  <Icon className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-foreground truncate">
                      <span className="font-semibold">{authorName}</span>{" "}
                      <span className="text-muted-foreground">mentioned you</span>
                    </p>
                    {m.preview && (
                      <p className="text-[11px] text-muted-foreground truncate">
                        {m.preview}
                      </p>
                    )}
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                      {new Date(m.created_at).toLocaleString([], {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                  {!m.read_at && (
                    <span
                      className="w-1.5 h-1.5 rounded-full bg-primary self-center shrink-0"
                      aria-label="unread"
                    />
                  )}
                </div>
              );
              return href ? (
                <Link key={m.id} to={href} className="block">
                  {inner}
                </Link>
              ) : (
                <div key={m.id}>{inner}</div>
              );
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default MentionBell;
